// Shared instruction images configuration for external services
// Used by both OnboardingModal and CredentialsModal to ensure consistency

// CodeCombat Junior course ID
const CODECOMBAT_JUNIOR_COURSE_ID = '65c56663d2ca2055e65676af';

/**
 * Get instruction images for an activity type
 * @param {string} activityType - Type of activity (codecombat, duolingo, khan_academy)
 * @param {Array<string>} courses - Array of course IDs (used to determine CodeCombat Junior vs Senior)
 * @param {string} activitySubType - Sub-type of activity (e.g., "music" for Duolingo Music)
 * @returns {Array<{src: string, alt: string}>} Array of image objects
 */
export const getInstructionImages = (activityType, courses = [], activitySubType = null) => {
  // Duolingo ABC is a separate app with its own complete instruction flow
  if (activityType === 'duolingo' && activitySubType === 'abc') {
    return [
      { src: '/images/duolingo/abc/100.png', alt: 'Step 1' },
      { src: '/images/duolingo/abc/200.png', alt: 'Step 2' },
      { src: '/images/duolingo/abc/300.png', alt: 'Step 3' },
      { src: '/images/duolingo/abc/400.png', alt: 'Step 4' },
      { src: '/images/duolingo/abc/500.png', alt: 'Step 5' },
      { src: '/images/duolingo/abc/600.png', alt: 'Step 6' }
    ];
  }

  // Other Duolingo variants share base images (100, 200) with variant-specific images (300, 400, and 500 for english)
  if (activityType === 'duolingo' && activitySubType) {
    const baseImages = [
      { src: '/images/duolingo/100.png', alt: 'Step 1' },
      { src: '/images/duolingo/200.png', alt: 'Step 2' }
    ];
    // elochess uses the same chess images (same Duolingo Chess app)
    const imageFolder = activitySubType === 'elochess' ? 'chess' : activitySubType;
    const variantImages = [
      { src: `/images/duolingo/${imageFolder}/300.png`, alt: 'Step 3' },
      { src: `/images/duolingo/${imageFolder}/400.png`, alt: 'Step 4' }
    ];
    // English has an additional 5th image
    if (activitySubType === 'english') {
      variantImages.push({ src: '/images/duolingo/english/500.png', alt: 'Step 5' });
    }
    return [...baseImages, ...variantImages];
  }

  if (activityType === 'codecombat') {
    const isJunior = courses.includes(CODECOMBAT_JUNIOR_COURSE_ID);
    const baseImages = [
      { src: '/images/codecombat/100.png', alt: 'Step 1' },
      { src: '/images/codecombat/200.png', alt: 'Step 2' },
      { src: '/images/codecombat/300.png', alt: 'Step 3' },
      { src: '/images/codecombat/400.png', alt: 'Step 4' }
    ];
    return isJunior
      ? [
          ...baseImages,
          { src: '/images/codecombat/junior/500-junior.png', alt: 'Step 5' },
          { src: '/images/codecombat/junior/600-junior.png', alt: 'Step 6' },
          { src: '/images/codecombat/junior/700-junior.png', alt: 'Step 7' },
          { src: '/images/codecombat/junior/800-junior.png', alt: 'Step 8' },
          { src: '/images/codecombat/junior/900-junior.png', alt: 'Step 9' }
        ]
      : [
          ...baseImages,
          { src: '/images/codecombat/senior/500-senior.png', alt: 'Step 5' },
          { src: '/images/codecombat/senior/600-senior.png', alt: 'Step 6' },
          { src: '/images/codecombat/senior/700-senior.png', alt: 'Step 7' }
        ];
  }

  if (activityType === 'duolingo') {
    // Fallback for duolingo without sub-type (uses english variant)
    return [
      { src: '/images/duolingo/100.png', alt: 'Step 1' },
      { src: '/images/duolingo/200.png', alt: 'Step 2' },
      { src: '/images/duolingo/english/300.png', alt: 'Step 3' },
      { src: '/images/duolingo/english/400.png', alt: 'Step 4' }
    ];
  }

  if (activityType === 'khan_academy') {
    return [
      { src: '/images/khan/100.png', alt: 'Step 1' },
      { src: '/images/khan/200.png', alt: 'Step 2' },
      { src: '/images/khan/300.png', alt: 'Step 3' },
      { src: '/images/khan/400.png', alt: 'Step 4' },
      { src: '/images/khan/500.png', alt: 'Step 5' },
      { src: '/images/khan/600.png', alt: 'Step 6' }
    ];
  }

  if (activityType === 'brilliant') {
    return [
      { src: '/images/brilliant/100.png', alt: 'Step 1' },
      { src: '/images/brilliant/200.png', alt: 'Step 2' },
      { src: '/images/brilliant/300.png', alt: 'Step 3' }
    ];
  }

  if (activityType === 'art_workout') {
    // Base image (settings/sign-in) is common, second image depends on sub_type
    const variant = activitySubType || 'practice';
    return [
      { src: '/images/art_workout/100.png', alt: 'Step 1' },
      { src: `/images/art_workout/${variant}/200.png`, alt: 'Step 2' }
    ];
  }

  return [];
};

/**
 * Check if an activity type has instructions
 * @param {string} activityType - Type of activity
 * @param {string} activitySubType - Sub-type of activity (e.g., "music")
 * @returns {boolean}
 */
export const hasInstructions = (activityType, activitySubType = null) => {
  // All duolingo variants (english, music, chess) have instructions
  if (activityType === 'duolingo') return true;
  return ['codecombat', 'khan_academy', 'brilliant', 'art_workout'].includes(activityType);
};
